/* global SideDrawer, initLazyImages */

// TAG FILTER INTEGRATION:
// This facet-filters system is integrated with collection-tag-pills.js to support
// combined filtering using both URL path-based tags (/collections/handle/tag1+tag2)
// and query parameter-based native filters (?filter.p.brand=Nike).
// The system preserves tag paths when applying native filters in any order.

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.facetFiltersUpdatedHandler) {
        document.removeEventListener('on:facet-filters:updated', this.facetFiltersUpdatedHandler);
      }

      if (this.clickOutsideHandler) {
        document.removeEventListener('click', this.clickOutsideHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();

      // Collapse filters by default in horizontal layout (desktop)
      this.collapseHorizontalFilters();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);

      // Listen for updates from the tag pills system to reinitialize listeners
      this.facetFiltersUpdatedHandler = this.facetFiltersUpdatedHandler || this.handleExternalUpdate.bind(this);
      document.addEventListener('on:facet-filters:updated', this.facetFiltersUpdatedHandler);
    }

    /**
     * Collapse filters by default when in horizontal layout (desktop)
     */
    collapseHorizontalFilters() {
      // Check if filters are in horizontal layout (has parent with class)
      const filtersTop = this.closest('.main-products-grid__filters-top');
      if (filtersTop && window.innerWidth >= 1024) {
        // Close all filter details except active filters
        const filterDetails = this.querySelectorAll('.facets__filters .disclosure');
        filterDetails.forEach(detail => {
          detail.removeAttribute('open');
        });
        
        // Add click outside to close for horizontal filters
        this.setupClickOutsideToClose();
      }
    }

    /**
     * Setup click outside to close filter dropdowns in horizontal layout
     */
    setupClickOutsideToClose() {
      const filtersTop = this.closest('.main-products-grid__filters-top');
      if (!filtersTop || this.clickOutsideHandler) return;

      this.clickOutsideHandler = (event) => {
        // Only on desktop
        if (window.innerWidth < 1024) return;

        // Ignore "show more" — removing the button during click breaks contains() checks
        if (event.target.closest('.js-show-more')) return;

        const filterDetails = this.querySelectorAll('.facets__filters .disclosure[open]');

        filterDetails.forEach((detail) => {
          if (!detail.contains(event.target)) {
            detail.removeAttribute('open');
          }
        });
      };

      document.addEventListener('click', this.clickOutsideHandler);
    }

    /**
     * Handles external updates to the form (e.g., from tag pills system)
     */
    handleExternalUpdate(evt) {
      // Only reinitialize if this event came from an external source (not from this component)
      if (evt && evt.detail && evt.detail.source === 'facet-filters') return;
      
      console.log('[Facet Filters] External update detected, reinitializing...');
      // Re-get form reference in case it was replaced
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      // Reinitialize elements and listeners
      this.addElements();
      this.addListeners();
    }

    /**
     * Extracts the tag path from the current URL.
     * URL structure: /collections/handle/tag1+tag2
     * Returns: { basePath: '/collections/handle', tagPath: '/tag1+tag2', tags: ['tag1', 'tag2'] }
     * If no tags: { basePath: '/collections/handle', tagPath: '', tags: [] }
     */
    getTagPathInfo() {
      const pathParts = window.location.pathname.split('/').filter(part => part.length > 0);
      
      if (pathParts[0] === 'collections' && pathParts.length >= 2) {
        const basePath = `/collections/${pathParts[1]}`;
        
        if (pathParts.length >= 3) {
          const tagPath = `/${pathParts[2]}`;
          const tags = pathParts[2].split('+');
          return { basePath, tagPath, tags };
        }
        
        return { basePath, tagPath: '', tags: [] };
      }
      
      // Not a collection page, return current pathname as-is
      return { basePath: window.location.pathname, tagPath: '', tags: [] };
    }

    /**
     * Builds the full URL path with tags preserved.
     * @param {object} tagInfo - Tag information from getTagPathInfo()
     * @returns {string} - Full path with tags (e.g., '/collections/handle/tag1+tag2')
     */
    buildPathWithTags(tagInfo) {
      return tagInfo.basePath + tagInfo.tagPath;
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        // Extract search params from the button's href, but preserve current tag path
        const buttonUrl = new URL(evt.target.href);
        this.applyFilters(buttonUrl.searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        evt.preventDefault();
        evt.stopPropagation();

        const filter = target.closest('.filter');
        const disclosure = target.closest('details');
        target.hidden = true;

        filter.querySelectorAll('li.js-hidden').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }

        // Keep the dropdown open after expanding options (desktop horizontal layout)
        if (disclosure) {
          disclosure.setAttribute('open', '');
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      // Extract search params from the link's href, but preserve current tag path
      const linkUrl = new URL(evt.target.href);
      this.applyFilters(linkUrl.searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      let pathWithTags = window.location.pathname;
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) {
          customPagination.dataset.pauseInfiniteScroll = 'true';

          // Abort in-flight infinite-scroll requests to avoid stale results being appended
          // after new filter results are rendered.
          if (customPagination.controller) {
            customPagination.controller.abort();
          }

          if (customPagination.intersectingTimer) {
            clearInterval(customPagination.intersectingTimer);
          }

          if (customPagination.paginationObserver) {
            customPagination.paginationObserver.disconnect();
          }
        }

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        if (closeBtn) {
          closeBtn.ariaDisabled = 'true';
          closeBtn.classList.add('is-loading');
        }

        // Get tag path info to preserve tags in the URL
        const tagInfo = this.getTagPathInfo();
        pathWithTags = this.buildPathWithTags(tagInfo);
        
        // Debug logging
        if (tagInfo.tags.length > 0) {
          console.log('[Facet Filters] Preserving tags in URL:', tagInfo.tags);
          console.log('[Facet Filters] Path with tags:', pathWithTags);
        }

        // Use Section Rendering API for the request, if possible.
        // IMPORTANT: Use pathWithTags instead of window.location.pathname to preserve tag filters
        let fetchUrl = `${pathWithTags}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          const isHorizontalLayout = this.closest('.main-products-grid__filters-top') && window.innerWidth >= 1024;
          
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              // In horizontal layout, keep all filter dropdowns closed
              if (isHorizontalLayout && !existingFilter.closest('.facets__active-filters')) {
                target.open = false;
              } else {
                target.open = existingFilter.open;
              }
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.setAttribute('hidden', '');
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          if (closeBtn) {
            const nextCloseBtn = tmpl.content.querySelector('.js-close-drawer-mob');
            if (nextCloseBtn) closeBtn.innerText = nextCloseBtn.innerText;
          }

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();

          // Reinitialize lazyload images after filters applied.
          if (typeof initLazyImages === 'function') initLazyImages();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL with tags preserved.
          if (updateUrl) FacetFilters.updateURL(searchParams, pathWithTags);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          if (closeBtn) {
            closeBtn.classList.remove('is-loading');
            closeBtn.removeAttribute('aria-disabled');
          }

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated', { 
            bubbles: true,
            detail: { source: 'facet-filters' }
          }));
        }
      } catch (error) {
        console.warn(error);

        // Fallback: if async filtering fails for any reason, use full-page navigation
        // so filtering still works for shoppers.
        const fallbackUrl = `${pathWithTags}${searchParams ? '?'.concat(searchParams) : ''}`;
        window.location.href = fallbackUrl;
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     * @param {string} [pathWithTags] - Optional path with tags to use instead of window.location.pathname
     */
    static updateURL(searchParams, pathWithTags) {
      const pathname = pathWithTags || window.location.pathname;
      window.history.pushState(
        { searchParams },
        '',
        `${pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
